import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { MelodicCurvePlayer } from "@/components/et/MelodicCurvePlayer";
import { NextUp } from "@/components/Callouts";

export default function EarTraining6() {
  const { lesson, course, accent } = findLesson("ear-training", "6");
  return (
    <LessonShell
      course={course}
      courseSlug="ear-training"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/ear-training/ET6-Melodic-Curves-2-Animated.mp4"
        caption="The melodic-transcription pipeline — hear · curve · intervals · notate"
        hint="≈ 24 sec · watch first"
      />
      <Chapter number="06" eyebrow="Ear Training · Chapter Six" title="Melodic curves — the practice drills">
        <Prose>
          <p>
            Theory becomes ear training only when you actually drill. This chapter is a
            workout: five sets of melodic curves, ranging from two notes to seven, each one
            taken from Jason's notebook. Click each dot to hear it, then try to write the
            curve yourself before playing it back.
          </p>
        </Prose>

        <MTQuote>
          The most important skill for a musician on this earth right now is to transcribe
          music well. Transcription is a function of how well you know music and how well
          your mind is equipped with digesting the data.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="Set 1 · Two notes" title="The smallest curve possible">
        <Prose>
          <p>Two notes is just one interval. Pick the right interval and the curve is done.
            Listen to each, decide higher/lower/same, then identify the interval.</p>
        </Prose>

        <MelodicCurvePlayer notes={[{ pitch: "C4" }, { pitch: "E4" }]} title="Curve 1.1 (M3 ↑)" />
        <MelodicCurvePlayer notes={[{ pitch: "G4" }, { pitch: "C4" }]} title="Curve 1.2 (P5 ↓)" />

        <NoteImage
          src="/notes/ear-training/finals/06b-2-note-set-exercises.png"
          caption="Jason's 2-note exercises. Each curve has exactly one interval. Identify it (M3, P5, m2, octave, unison, P4) and you've completed the transcription."
        />
      </Chapter>

      <Chapter eyebrow="Set 2 · Three notes" title="Two intervals, one shape">
        <MelodicCurvePlayer notes={[{ pitch: "A3" }, { pitch: "D4" }, { pitch: "C#4" }]} title="Curve 2.1" />
        <MelodicCurvePlayer notes={[{ pitch: "A4" }, { pitch: "G4" }, { pitch: "E4" }]} title="Curve 2.2" />

        <NoteImage
          src="/notes/ear-training/finals/06a-3-note-set-exercises.png"
          caption="Five 3-note exercises from Jason's notebook. Sketch the curve, identify each interval, then check by playing the recording."
        />
      </Chapter>

      <Chapter eyebrow="Set 3 · Four notes" title="Now the shape gets interesting">
        <MelodicCurvePlayer notes={[{ pitch: "A3" }, { pitch: "C4" }, { pitch: "F#4" }, { pitch: "D4" }]} title="Curve 3.1" />
        <MelodicCurvePlayer notes={[{ pitch: "G4" }, { pitch: "Db4" }, { pitch: "F4" }, { pitch: "Eb4" }]} title="Curve 3.2" />

        <NoteImage
          src="/notes/ear-training/finals/06c-4-note-set-exercises.png"
          caption="Four-note sets — five curves, all in different starting positions. Pay attention to whether the shape is mostly ascending, mostly descending, or has a peak/trough."
        />
      </Chapter>

      <Chapter eyebrow="Set 4 · Random notes" title="Free-form melodic shapes">
        <Prose>
          <p>Now the curves get ambitious. Five to seven notes, jumping around the staff —
            but every note still belongs to a single key. Use the ear-training-1 method
            (find the tonic) before you try to identify the intervals.</p>
        </Prose>

        <MelodicCurvePlayer
          notes={[
            { pitch: "A4" }, { pitch: "F#4" }, { pitch: "G4" },
            { pitch: "C#4" }, { pitch: "D4" }, { pitch: "A4" },
          ]}
          title="Curve 4.1 — A Mixolydian"
        />

        <NoteImage
          src="/notes/ear-training/finals/06d-random-notes-1-and-2.png"
          caption="Random-note curve 1 (A&apos; F♯ G C♯ D A): home note A, but the G is natural — A major would need G♯ — so this is A Mixolydian, the same notes as D major. That one accidental IS the diagnostic. Curve 2 (F♯ D C♯ C♯ B A): the same note set, descending mostly."
        />

        <NoteImage
          src="/notes/ear-training/finals/06e-random-notes-3-and-4.png"
          caption="More 6+ note curves. Curve 3 in D♭ major (D♭ A♭ F E♭ D♭). Curve 4 in D major (A C♯ D D G F♯ E D)."
        />

        <NoteImage
          src="/notes/ear-training/finals/06f-random-notes-5-extended.png"
          caption="Curve 5 — an extended ascending curve (E F♯ G♯ B C♯ B F♯ E') climbing through a major scale. Notice the descent pattern at the end uses the same notes in reverse — typical of question-answer phrasing."
        />
      </Chapter>

      <NextUp
        to="/lesson/ear-training-7"
        label="Chapter 7 · Listening to Chords - I IV V"
        why="From single melody notes we move to chords. The three primary chords (I, IV, V) carry a huge share of pop, rock, folk and country — and learning to hear which is which is a superpower."
      />
    </LessonShell>
  );
}
